import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCourseModule } from './useCourseModule';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, FolderOpen, Lock } from 'lucide-react';
import { ModuleCourseStats, ModuleCourseCard } from './ModuleCourseCards';

const EMPTY_STATS = { total: 0, in_progress: 0, completed: 0, not_started: 0 };

export default function ModuleMyCourses() {
  const { api } = useAuth();
  const { module, notFound, apiBase, learnerBase } = useCourseModule();
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const statusParam = activeTab !== 'all' ? `?status=${activeTab}` : '';
      const response = await api.get(`${apiBase}/builder-courses/my-courses${statusParam}`);
      setCourses(response.data.courses || []);
      setStats(response.data.stats || EMPTY_STATS);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load your courses');
    } finally {
      setLoading(false);
    }
  }, [api, apiBase, activeTab]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  if (notFound || error) {
    return (
      <MainLayout>
        <Card data-testid="module-courses-unavailable">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Lock className="h-14 w-14 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">This training is not available</h3>
            <p className="text-muted-foreground text-center mt-1">
              {notFound ? 'This course module no longer exists.' : error}
            </p>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="module-my-courses">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="module-my-courses-title">
            {module?.name || 'My Courses'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {module?.description || 'Courses assigned to you'}
          </p>
        </div>

        <ModuleCourseStats stats={stats} />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="module-tab-all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="in_progress" data-testid="module-tab-in-progress">In Progress ({stats.in_progress})</TabsTrigger>
            <TabsTrigger value="completed" data-testid="module-tab-completed">Completed ({stats.completed})</TabsTrigger>
            <TabsTrigger value="not_started" data-testid="module-tab-not-started">Not Started ({stats.not_started})</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : courses.length === 0 ? (
          <Card data-testid="module-no-courses">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FolderOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No courses found</h3>
              <p className="text-muted-foreground text-center mt-1">
                {activeTab === 'all'
                  ? "You haven't been assigned any courses in this module yet."
                  : `No courses with "${activeTab.replace('_', ' ')}" status.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(course => (
              <ModuleCourseCard key={course.id} course={course} to={`${learnerBase}/course/${course.id}`} />
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
